import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getStoredUser, getStoredToken, setSession, clearSession } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(getStoredToken());
  const [bootstrapped, setBootstrapped] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("odl_user", JSON.stringify(res.data));
    } catch (err) {
      // If token is invalid, clear it. Otherwise keep cached user.
      if (err?.response?.status === 401) {
        localStorage.removeItem("odl_token");
        localStorage.removeItem("odl_user");
        setUser(null);
        setToken(null);
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      refreshMe().finally(() => setBootstrapped(true));
    } else {
      setBootstrapped(true);
    }
  }, [token, refreshMe]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setSession(res.data.access_token, res.data.user);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refreshMe, bootstrapped }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
